<template>
  <div class="admin-dashboard">
    <NavBar />
    <div class="container-fluid py-5 px-4">
      <h2 class="mb-4">Admin Dashboard</h2>
      <div class="row">
        <div v-for="page in visiblePages" :key="page.route" class="col-md-4 mb-4">
          <div class="card h-100 shadow-sm admin-card" @click="$router.push({ name: page.route })">
            <div class="card-body d-flex flex-column align-items-start justify-content-between">
              <div>
                <div class="admin-card-icon mb-3">{{ page.icon }}</div>
                <h5 class="card-title">{{ page.title }}</h5>
                <p class="card-text text-muted small">{{ page.description }}</p>
              </div>
              <b-button variant="outline-primary" size="sm" class="mt-3">
                Open
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar';

const INTERNAL_HOME_PATH = '/';

export default {
  name: 'AdminDashboard',
  components: { NavBar },
  data() {
    return {
      adminPages: [
        {
          route: 'admin_customer_pricing',
          title: 'Customer Pricing',
          description: 'Edit daily price, white-label price, and custom cost lines per customer.',
          icon: '💰',
        },
        {
          route: 'admin_monthly_billing_summary',
          title: 'Monthly Billing Summary',
          description: 'View and charge managers for their monthly usage.',
          icon: '📋',
        },
        {
          route: 'ride_manager_billing',
          title: 'Ride Manager Billing',
          description: 'View ride-level billing data and PDF export history per manager.',
          icon: '🧾',
        },
        {
          route: 'admin_categories',
          title: 'Category Management',
          description: 'Add and manage attraction/ride categories.',
          icon: '🗂️',
        },
        {
          route: 'device_management',
          title: 'Device Management',
          description: 'Monitor and manage registered devices for fraud detection.',
          icon: '📱',
        },
        {
          route: 'one_time_access_usage',
          title: 'One-Time Access Usage',
          description: 'View one-time access code usage and statistics.',
          icon: '🔑',
        },
        {
          route: 'request-password-reset',
          title: 'Reset Password',
          description: 'Request a password reset link. Enter the user\'s email to send them a reset email. The link expires after a short time.',
          icon: '🔒',
        },
        {
          route: 'admin_block_user',
          title: 'Block / Unblock User',
          description: 'Block a user from accessing the entire system, or unblock them.',
          icon: '🚫',
        },
        {
          route: 'admin manual login',
          title: 'Manual Login',
          description: 'Log in manually as any user for admin/support purposes.',
          icon: '🔐',
        },
        {
          route: 'admin_accommodation_links',
          title: 'Accommodation Link Review',
          description: 'Review and update accommodation booking links to affiliate links, one at a time.',
          icon: '🔗',
        },
        {
          route: 'admin_ride_accommodation_links',
          title: 'Ride Accommodation Links',
          description: 'Search by ride ID and update all accommodation links for that specific ride.',
          icon: '🏨',
        },
        {
          route: 'generate_ride_with_ai',
          title: 'Build a Ride with AI',
          description: 'Generate a whole ride from the admin attractions pool with AI.',
          icon: '🤖',
          // Guarded by beforeEnter: adminOnly in main.js.
          adminOnly: true,
        },
        {
          route: 'admin_whatsapp_conversations',
          title: 'WhatsApp Conversations',
          description: 'Read the full exchange with any traveller, both what they sent and what we sent back.',
          icon: '💬',
          // Guarded by beforeEnter: adminOnly in main.js. Hidden here too, so a
          // non-admin is never offered a card that would just bounce them home.
          adminOnly: true,
        },
        {
          route: 'admin_shared_place_images',
          title: 'Shared Place Images',
          description: 'Approve or reject the Pixabay images users picked for each place, so every ride can reuse them.',
          icon: '🖼️',
          // Guarded by beforeEnter: adminOnly in main.js.
          adminOnly: true,
        },
        {
          route: 'whatsapp_test',
          title: 'WhatsApp Test Send',
          description: 'Send a test WhatsApp message and list the approved templates. Needs WHATSAPP_TEST_ENABLED set on the server.',
          icon: '🧪',
          adminOnly: true,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['userId']),
    visiblePages() {
      // Same definition of admin as the route guard (main.js adminOnly), the
      // navbar (NavBar.isAdmin) and this component's own created() redirect:
      // user 1. Filtering here means a non-admin is never offered a card that
      // would only bounce them home.
      return this.adminPages.filter(page => !page.adminOnly || this.userId === 1);
    },
  },
  created() {
    if (this.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
    }
  },
};
</script>

<style scoped>
.admin-dashboard {
  font-size: 16px;
}
.admin-dashboard >>> h2 { font-size: 28px; }
.admin-dashboard >>> h5 { font-size: 18px; }
.admin-dashboard >>> p, .admin-dashboard >>> .card-text { font-size: 14px; }
.admin-dashboard >>> .btn { font-size: 14px; padding: 6px 14px; }
.admin-dashboard >>> .card-body { padding: 20px; }

.admin-card {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}
.admin-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.13) !important;
  transform: translateY(-2px);
}
.admin-card-icon {
  font-size: 32px;
}
</style>
